import { PartnerSelectOption } from '../models/partner-select-option';
import { SelectOption } from '../../../models/select-option';
import { DateRange } from '../models/date-range';
import { RequestResponse } from '../models/request-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../../models/empty-rows-descriptor';
import { accountSummary$ } from '../../../repositories/ams/ams.repository';
import { ALK_ROLES } from '../../../models/alk-roles';
import { composeRoles } from '../../../utils/compose-roles';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  createRequestsStatusOperator,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import {
  CloudProvider,
  ConfigurationSettingEntry,
  ConfigurationSettingsList,
  CopilotConfigOverride,
  CopilotLogList,
  MobileInstructionGroup,
  Partner,
  PartnerAccount,
  RemoteAction,
  User,
  WhitelistedConfigurationSetting,
} from '@tmx/data-access';
import { RowsDescriptor } from '@tmx/operandi';
import { getCurrentAuthenticatedUser } from '@tmx/auth';
import { combineLatest, map, Observable } from 'rxjs';

const hasAlkRole = composeRoles(ALK_ROLES);

interface DiagnosticsProps {
  partnerOptions: PartnerSelectOption[];
  accountOptions: SelectOption[];
  actionOptions: SelectOption[];
  vehicleGroupOptions: SelectOption[];
  configOverrideHistoricalOffset: number;
  totalCopilotConfigsHistorical: number;
  settingOptions: SelectOption[];
  actions: RemoteAction[];
  selectedPartner: string;
  selectedAccount: string;
  selectedPartnerName: string;
  selectedPartnerType: string;
  selectedAccountName: string;
  selectedDeviceId: string;
  selectedVehicleGroupOptions: SelectOption[];
  selectedAction: string;
  selectedImpactLevel: string;
  selectedSetting: string;
  assets: User[];
  totalAssets: number;
  metricLogResponse: CopilotLogList[];
  startDate: Date;
  dateRange: DateRange;
  accountAliases: Map<string, string>;
  accountCloudProviders: Map<string, CloudProvider>;
  copilotConfigs: WhitelistedConfigurationSetting[];
  copilotConfigsRowsDescriptor: RowsDescriptor;
  copilotConfigOverrides: CopilotConfigOverride[];
  copilotConfigsHistorical: ConfigurationSettingEntry[];
  settingsSourcedFromParentAccount: boolean;
  selectedRequestResponse: RequestResponse;
  mobileInstructions: MobileInstructionGroup;
  blocksRowsDescriptor: RowsDescriptor;
  selectedMobileInstructionId: number;
  fileId: string;
  overrideHistoryEntryRowsDescriptor: RowsDescriptor;
  dailyMetricsByAssetOffset: number;
  dailyMetricsByAssetQuery: string;
  dailyMetricsByAssetRowsDescriptor: RowsDescriptor;
}

const diagnosticsStore = createStore(
  { name: 'diagnostics' },
  withProps<DiagnosticsProps>({
    partnerOptions: [],
    accountOptions: [],
    actionOptions: [],
    vehicleGroupOptions: [],
    configOverrideHistoricalOffset: 0,
    totalCopilotConfigsHistorical: 0,
    settingOptions: [],
    actions: [],
    selectedPartner: '',
    selectedAccount: '',
    selectedPartnerName: '',
    selectedPartnerType: '',
    selectedAccountName: '',
    selectedDeviceId: '',
    selectedVehicleGroupOptions: [],
    selectedAction: '',
    selectedImpactLevel: 'impact-level-low',
    selectedSetting: '',
    assets: [],
    totalAssets: 0,
    metricLogResponse: [],
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    dateRange: new DateRange(new Date(), 6),
    accountAliases: new Map(),
    accountCloudProviders: new Map(),
    copilotConfigs: [],
    copilotConfigsRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    copilotConfigOverrides: [],
    copilotConfigsHistorical: [],
    settingsSourcedFromParentAccount: false,
    selectedRequestResponse: new RequestResponse(),
    mobileInstructions: {},
    blocksRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selectedMobileInstructionId: 0,
    fileId: '',
    overrideHistoryEntryRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    dailyMetricsByAssetOffset: 0,
    dailyMetricsByAssetQuery: '',
    dailyMetricsByAssetRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
  }),
  withRequestsStatus<
    | 'account'
    | 'action'
    | 'asset'
    | 'copilot configs'
    | 'copilot-configs-historical'
    | 'metric logs'
    | 'mobile-instruction-index-select'
    | 'mobile-instructions-add'
    | 'mobile-instructions-request'
    | 'partners'
    | 'retrieve-file'
    | 'send-overrides'
    | 'setting'
    | 'uploading'
    | 'vehicle-groups'
  >()
);

export const trackDiagnosticsRequestsStatus =
  createRequestsStatusOperator(diagnosticsStore);

export const partnerOptions$ = diagnosticsStore.pipe(
  select((state) => state.partnerOptions)
);

export const overrideHistoryEntryRowsDescriptor$ = diagnosticsStore.pipe(
  select((state) => state.overrideHistoryEntryRowsDescriptor)
);

export const accountOptions$ = diagnosticsStore.pipe(
  select((state) => state.accountOptions)
);

export const actionOptions$ = diagnosticsStore.pipe(
  select((state) => state.actionOptions)
);

export const vehicleGroupOptions$ = diagnosticsStore.pipe(
  select((state) => state.vehicleGroupOptions)
);

export const settingOptions$ = diagnosticsStore.pipe(
  select((state) => state.settingOptions)
);

export const selectedPartner$ = diagnosticsStore.pipe(
  select((state) => state.selectedPartner)
);

export const selectedPartnerType$ = diagnosticsStore.pipe(
  select((state) => state.selectedPartnerType)
);

export const selectedAccount$ = diagnosticsStore.pipe(
  select((state) => state.selectedAccount)
);

export function lookupAccountAlias(account: string) {
  return diagnosticsStore.getValue().accountAliases.get(account) ?? '';
}

export function lookupAccountCloudProvider(account: string) {
  return (
    diagnosticsStore.getValue().accountCloudProviders.get(account) ??
    getCurrentAuthenticatedUser().cloudProvider
  );
}

export const selectedDeviceId$ = diagnosticsStore.pipe(
  select((state) => state.selectedDeviceId)
);

export const selectedAction$ = diagnosticsStore.pipe(
  select((state) => state.selectedAction)
);

export const selectedImpactLevel$ = diagnosticsStore.pipe(
  select((state) => state.selectedImpactLevel)
);

export const selectedSetting$ = diagnosticsStore.pipe(
  select((state) => state.selectedSetting)
);

export const totalAssets$ = diagnosticsStore.pipe(
  select((state) => state.totalAssets)
);

export const mobileInstructions$ = diagnosticsStore.pipe(
  select((state) => state.mobileInstructions)
);

export const fileId$ = diagnosticsStore.pipe(select((state) => state.fileId));

export function returnAssetResponse() {
  const { assets, totalAssets } = diagnosticsStore.getValue();
  return { assets, totalAssets };
}

export const metricLogResponse$ = diagnosticsStore.pipe(
  select((state) => state.metricLogResponse)
);

export function returnMetricLogResponse() {
  return diagnosticsStore.getValue().metricLogResponse;
}

export function returnDateRange() {
  return diagnosticsStore.getValue().dateRange;
}

export const copilotConfigs$ = diagnosticsStore.pipe(
  select((state) => state.copilotConfigs)
);

export const copilotConfigsRowsDescriptor$ = diagnosticsStore.pipe(
  select((state) => state.copilotConfigsRowsDescriptor)
);

export const settingsSourcedFromParentAccount$ = diagnosticsStore.pipe(
  select((state) => state.settingsSourcedFromParentAccount)
);

export const copilotConfigsHistorical$ = diagnosticsStore.pipe(
  select((state) => state.copilotConfigsHistorical)
);

export const dailyMetricsByAssetRowsDescriptor$ = diagnosticsStore.pipe(
  select((state) => state.dailyMetricsByAssetRowsDescriptor)
);

export const instructions$ = diagnosticsStore.pipe(
  select((state) => state.mobileInstructions)
);

export const blocksRowsDescriptor$ = diagnosticsStore.pipe(
  select((state) => state.blocksRowsDescriptor)
);

export const partnerStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('partners')
);

export const actionStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('action')
);

export const accountStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('account')
);

export const assetStatus$ = diagnosticsStore.pipe(selectRequestStatus('asset'));

export const metricLogStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('metric logs')
);

export const settingStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('setting')
);

export const copilotConfigsStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('copilot configs')
);

export const copilotConfigsHistoricalStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('copilot-configs-historical')
);

export const mobileInstructionsStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('mobile-instructions-add')
);

export const mobileInstructionsRequestStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('mobile-instructions-request')
);

export const sendOverridesStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('send-overrides')
);

export const uploadingStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('uploading')
);

export const retrieveFileStatus$ = diagnosticsStore.pipe(
  selectRequestStatus('retrieve-file')
);

export const partnerAutoSelect$: Observable<PartnerSelectOption | null> =
  combineLatest([partnerOptions$, accountSummary$]).pipe(
    map(([partnerOptions, accountSummary]) => {
      if (
        !hasAlkRole(getCurrentAuthenticatedUser().roles) ||
        partnerOptions.length === 0
      ) {
        return null;
      }

      const partnerOption = partnerOptions.find(
        (option) => option.value === accountSummary.partnerId
      );
      return partnerOption ?? null;
    })
  );

export const accountAutoSelect$: Observable<string | null> =
  accountSummary$.pipe(
    map(({ companyName }) =>
      hasAlkRole(getCurrentAuthenticatedUser().roles) ? companyName : null
    )
  );

export const selectedVehicleGroupOptions$ = diagnosticsStore.pipe(
  select(({ selectedVehicleGroupOptions }) => selectedVehicleGroupOptions)
);

export function updateSendOverridesStatus() {
  diagnosticsStore.update(updateRequestStatus('send-overrides', 'success'));
}

export function updateInstructionRequestStatus() {
  diagnosticsStore.update(
    updateRequestStatus('mobile-instructions-request', 'success')
  );
}

export function updateRetrieveFileStatus() {
  diagnosticsStore.update(updateRequestStatus('retrieve-file', 'success'));
}

export function updatePartnerOptions(partners: Partner[]) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      partnerOptions: partners.map(
        (option) =>
          new PartnerSelectOption(
            option.partnerName,
            option.partnerId,
            option.integrationType
          )
      ),
    }),
    updateRequestStatus('partners', 'success')
  );
}

export function updateAccountOptions(accounts: PartnerAccount[]) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      accountOptions: accounts.map(
        (option) => new SelectOption(option.name, String(option.id))
      ),
      selectedAccount: '',
      accountAliases: new Map(
        accounts.map(({ id, alias }) => [String(id), alias])
      ),
      accountCloudProviders: new Map(
        accounts.map(({ id, cloudProvider }) => [String(id), cloudProvider])
      ),
    }),
    updateRequestStatus('account', 'success')
  );
}

export function updateMobileInstructions(
  mobileInstructions: MobileInstructionGroup
) {
  if (mobileInstructions.blocks) {
    diagnosticsStore.update(
      (state) => ({
        ...state,
        mobileInstructions,
      }),
      updateRequestStatus(
        'mobile-instructions-add',
        mobileInstructions.blocks.length > 0 ? 'success' : 'pending'
      )
    );
  }
}

export function updateDailyMetricsByAssetOffset(offset: number) {
  diagnosticsStore.update((state) => ({
    ...state,
    dailyMetricsByAssetOffset: offset,
  }));
}

export function updateDailyMetricsByAssetQuery(query: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    dailyMetricsByAssetQuery: query,
  }));
}

export function updateDailyMetricsByAssetRowsDescriptor(
  dailyMetricsByAsset: RowsDescriptor
) {
  diagnosticsStore.update((state) => ({
    ...state,
    dailyMetricsByAssetRowsDescriptor: dailyMetricsByAsset,
  }));
}

export function updateBlocksRowsDescriptor(
  blocksRowsDescriptor: RowsDescriptor
) {
  diagnosticsStore.update((state) => ({
    ...state,
    blocksRowsDescriptor,
  }));
}

export function updateScope() {
  diagnosticsStore.update((state) => {
    if (Object.keys(state.mobileInstructions).length === 0) {
      return state;
    }

    const {
      selectedPartner,
      selectedAccount,
      selectedDeviceId,
      selectedVehicleGroupOptions,
    } = state;

    const mobileInstructions = {
      ...state.mobileInstructions,
      partnerId: selectedPartner,
      accountId: selectedAccount,
      deviceId: selectedDeviceId,
      assetId: selectedDeviceId,
      vehicleGroupIds: selectedVehicleGroupOptions.map(({ value }) =>
        Number.parseInt(value, 10)
      ),
    } as MobileInstructionGroup;
    return { ...state, mobileInstructions };
  });
}

export function updateFileId(fileId: string) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      fileId,
    }),
    updateRequestStatus('uploading', 'success')
  );
}

export function updateMobileInstructionIndex(
  selectedMobileInstructionId: number
) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      selectedMobileInstructionId,
    }),
    updateRequestStatus('mobile-instruction-index-select', 'success')
  );
}

export function updateActionOptions(actions: RemoteAction[]) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      actionOptions: actions.map(
        (action) =>
          new SelectOption(
            action.description ?? '',
            action.message,
            `impact-level-${action.impactLevel.toLowerCase()}`
          )
      ),
      actions,
      selectedAction: '',
    }),
    updateRequestStatus('action', 'success')
  );
}

export function updateVehicleGroupOptions(vehicleGroupOptions: SelectOption[]) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      vehicleGroupOptions,
    }),
    updateRequestStatus('vehicle-groups', 'success')
  );
}

export function updateConfigOverrideHistoricalOffset(offset: number) {
  diagnosticsStore.update((state) => ({
    ...state,
    configOverrideHistoricalOffset: offset,
  }));
}

export function updateSettingOptions(data: ConfigurationSettingsList) {
  const settings = data.configurationSettings;
  diagnosticsStore.update(
    (state) => ({
      ...state,
      settingOptions: settings.map(
        (setting) =>
          new SelectOption(setting.settingName, String(setting.settingId))
      ),
      selectedSetting: '',
      settingsSourcedFromParentAccount: data.settingsSourcedFromParentAccount,
    }),
    updateRequestStatus('setting', 'success')
  );
}

export function updateSelectedPartner(selectedPartner: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedPartner,
  }));
}

export function updateSelectedPartnerType(selectedPartnerType: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedPartnerType,
  }));
}

export function updateSelectedPartnerName(selectedPartnerName: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedPartnerName,
  }));
}

export function updateSelectedAccountName(selectedAccountName: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedAccountName,
  }));
}

export function updateSelectedAccount(selectedAccount: string) {
  if (selectedAccount === '') {
    updateSelectedAccountName('');
  }
  diagnosticsStore.update((state) => ({
    ...state,
    selectedAccount,
  }));
}

export function updateSelectedDeviceId(selectedDeviceId: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedDeviceId,
  }));
}

export function updateSelectedVehicleGroupOptions(
  selectedVehicleGroupOptions: SelectOption[]
) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedVehicleGroupOptions,
  }));
}

export function updateSelectedAction(selectedAction: string) {
  const selectedImpactLevel = `impact-level-${
    diagnosticsStore
      .getValue()
      .actions.find((action) => action.description === selectedAction)
      ?.impactLevel ?? 0
  }`;

  diagnosticsStore.update((state) => ({
    ...state,
    selectedAction,
    selectedImpactLevel,
  }));
}

export function updateSelectedSetting(selectedSetting: string) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedSetting,
  }));
}

export function updateAssets(assets: User[], totalAssets: number) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      assets,
      totalAssets,
    }),
    updateRequestStatus('asset', 'success')
  );
}

export function updateMetricLogResponse(metricLogResponse: CopilotLogList[]) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      metricLogResponse,
    }),
    updateRequestStatus('metric logs', 'success')
  );
}

export function updateStartDate(startDate: Date) {
  diagnosticsStore.update((state) => ({
    ...state,
    startDate,
  }));
}

export function updateDateRange(dateRange: DateRange) {
  diagnosticsStore.update((state) => ({
    ...state,
    dateRange,
  }));
}

export function updateCopilotConfigs(
  copilotConfigs: WhitelistedConfigurationSetting[]
) {
  copilotConfigs.sort((config1, config2) => {
    const sortOrderByGroup =
      config1.configurationGroup > config2.configurationGroup
        ? 1
        : config1.configurationGroup < config2.configurationGroup
          ? -1
          : 0;

    if (sortOrderByGroup === 0) {
      return config1.configurationKey > config2.configurationKey
        ? 1
        : config1.configurationKey < config2.configurationKey
          ? -1
          : 0;
    }

    return sortOrderByGroup;
  });

  diagnosticsStore.update(
    (state) => ({
      ...state,
      copilotConfigs,
    }),
    updateRequestStatus('copilot configs', 'success')
  );
}

export function updateCopilotConfigsRowsDescriptor(
  copilotConfigsRowsDescriptor: RowsDescriptor
) {
  diagnosticsStore.update((state) => ({
    ...state,
    copilotConfigsRowsDescriptor,
  }));
}

export function updateOverrideHistoryEntryRowsDescriptor(
  overrideHistoryEntryRowsDescriptor: RowsDescriptor
) {
  diagnosticsStore.update((state) => ({
    ...state,
    overrideHistoryEntryRowsDescriptor,
  }));
}

export function updateCopilotConfigsHistorical(
  copilotConfigsHistorical: ConfigurationSettingEntry[],
  totalCopilotConfigsHistorical: number
) {
  diagnosticsStore.update(
    (state) => ({
      ...state,
      copilotConfigsHistorical,
      totalCopilotConfigsHistorical,
    }),
    updateRequestStatus('copilot-configs-historical', 'success')
  );
}

export function updateSelectedRequestResponse(
  selectedRequestResponse: RequestResponse
) {
  diagnosticsStore.update((state) => ({
    ...state,
    selectedRequestResponse,
  }));
}

export function resetDiagnosticsStore() {
  diagnosticsStore.reset();
}

export const hasSettingOptions$ = diagnosticsStore.pipe(
  select((state) => state.settingOptions.length > 0)
);

export const hasOverrides$ = diagnosticsStore.pipe(
  select((state) => state.copilotConfigOverrides.length > 0)
);

export function updateCopilotConfigOverrides(
  copilotConfigOverrides: CopilotConfigOverride[]
) {
  if (
    copilotConfigOverrides.length < 1 &&
    diagnosticsStore.getValue().copilotConfigOverrides.length < 1
  ) {
    return;
  }

  diagnosticsStore.update((state) => ({
    ...state,
    copilotConfigOverrides,
  }));
}

export function returnSelectedRequestResponse() {
  return diagnosticsStore.getValue().selectedRequestResponse;
}

export const copilotConfigOverrides$ = diagnosticsStore.pipe(
  select((state) => state.copilotConfigOverrides)
);

export function returnCopilotConfigOverrides() {
  return diagnosticsStore.getValue().copilotConfigOverrides;
}

export function returnSelectedSetting() {
  return diagnosticsStore.getValue().selectedSetting;
}

export function returnSelectedAccount() {
  return diagnosticsStore.getValue().selectedAccount;
}

export function returnSelectedDeviceId() {
  return diagnosticsStore.getValue().selectedDeviceId;
}

export function returnSelectedPartner() {
  return diagnosticsStore.getValue().selectedPartner;
}

export const diagnosticsStoreSnapshot = () => diagnosticsStore.getValue();

/**
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_afs)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_tile_cache)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_custom_places)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_logs)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_overrides)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_region_save)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_routesync_files)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_routing_profiles)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_save)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_sites)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_user_configuration)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_delete_user_custom_ui_configuration)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_factory_reset)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_log)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_request_api_usage)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_all_log)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_rebuild_override)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_trip_history)
 * t(diagnostics.requestCopilotAction.confirmRequestModalAction_trip_recalculation)
 * t(diagnostics.diagnostics.toastErrorLoadAccounts)
 * t(diagnostics.diagnostics.toastErrorLoadActions)
 * t(diagnostics.diagnostics.toastErrorLoadAssets)
 * t(diagnostics.diagnostics.toastErrorLoadMetricLogs)
 * t(diagnostics.diagnostics.toastErrorLoadPartners)
 * t(diagnostics.diagnostics.toastErrorRequestSent)
 * t(diagnostics.diagnostics.toastErrorRetrievingFile)
 * t(diagnostics.diagnostics.toastErrorSendRequest)
 * t(diagnostics.diagnostics.toastErrorLoadSettings)
 * t(diagnostics.diagnostics.toastErrorConfigOverrideHistorical)
 * t(diagnostics.diagnostics.toastSuccessOverridesSent)
 * t(diagnostics.diagnostics.toastErrorOverrides)
 */
